'use client';

import { useRouter } from 'next/navigation';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import { Format } from '../../../../lib/dateFns';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { newDate } from '../../../../utils/date';
import { formatDateCommon } from '../../../../utils/formatters';
import type { LotteryEventIndexPaging } from '../../types';
import styles from './TradeList.module.scss';

export type TradeListProps = {
  eventList: Array<LotteryEventIndexPaging>;
};

function TradeList({ eventList }: TradeListProps) {
  const router = useRouter();

  const handleTradeAction = (lotteryId: number, action: 'trade' | 'history', event: React.MouseEvent) => {
    event.stopPropagation();
    if (action === 'trade') {
      //@ts-ignore
      router.push(`/trade/${lotteryId}`);
    } else {
      //@ts-ignore
      router.push(`/trade/${lotteryId}/history`);
    }
  };

  const handleItemClick = (lotteryId: number) => {
    router.push(`/lotteries/${lotteryId}`);
  };

  const isWithinTradePeriod = (tradeAt: string | undefined): boolean => {
    if (!tradeAt) return false;

    try {
      const tradeDate = newDate(tradeAt);
      const now = newDate();
      return now <= tradeDate;
    } catch {
      return false;
    }
  };

  const renderTradePeriodText = (item: LotteryEventIndexPaging) => {
    if (!item.isEnabledTrade) {
      return null;
    }

    const isWithinPeriod = isWithinTradePeriod(item.tradeAt);
    const textColor = isWithinPeriod ? utilsStyles.primaryColor : utilsStyles.grayColor;
    const text = isWithinPeriod
      ? `交換 / 取消可能期間：${formatDateCommon(item.tradeAt, Format.dateTimeLotteryJa)}まで`
      : '交換可能期間：終了';

    return (
      <div className={`${utilsStyles.mtXSmall}`}>
        <p className={`${utilsStyles.textBold} ${styles.tradePeriod} ${textColor}`}>{text}</p>
      </div>
    );
  };

  const renderTradeButtons = (item: LotteryEventIndexPaging) => {
    const isWithinPeriod = isWithinTradePeriod(item.tradeAt);
    const buttonWrapperClass = `${utilsStyles.mtXSmall} ${isWithinPeriod ? styles.tradeButtonWrapper : ''}`;

    if (isWithinPeriod) {
      return (
        <div className={buttonWrapperClass}>
          {item.hasTradeHistory && (
            <button
              className={styles.primaryButton}
              type='button'
              onClick={(e) => handleTradeAction(item.id, 'history', e)}
            >
              交換履歴を確認
            </button>
          )}
          <button className={styles.tradeButton} type='button' onClick={(e) => handleTradeAction(item.id, 'trade', e)}>
            景品を交換
          </button>
        </div>
      );
    }

    if (item.hasTradeHistory) {
      return (
        <div className={buttonWrapperClass}>
          <button
            type='button'
            className={styles.primaryButton}
            onClick={(e) => handleTradeAction(item.id, 'history', e)}
          >
            交換履歴を確認
          </button>
        </div>
      );
    }

    return (
      <div className={buttonWrapperClass}>
        <button type='button' className={styles.noTradeButton} disabled={true}>
          交換不可
        </button>
      </div>
    );
  };

  return (
    <ul className={styles.lotteryCollectionEvents}>
      {eventList?.map((item: LotteryEventIndexPaging) => (
        <li key={item.id}>
          <div className={styles.itemLink} onClick={() => handleItemClick(item.id)}>
            <div className={styles.itemImage}>
              <ImageView alt={item.name} src={item.imageUrl} isFreeSize={true} />
            </div>
            <div className={styles.itemDetail}>
              <div className={styles.itemDetailWrapper}>
                <h2 className={`${styles.itemTitle} ${utilsStyles.textBold} ${utilsStyles.mtXSmall}`}>{item.name}</h2>
                {renderTradePeriodText(item)}
                {renderTradeButtons(item)}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default TradeList;
